import { Container } from "unstated"

import api from "../utils/api"
import deserialize from "../utils/deserialize"
import debounce from "../utils/debounce"
import get from "lodash/get"

const getAvailable = ({ available }) => available

const BILLING_CYCLES = [
  {
    available: true,
    code: "weekly",
    title: "weekly",
  },
  {
    available: true,
    code: "monthly",
    title: "monthly",
  },
]

const PROXY_LOCATION_TYPES = [
  {
    id: "multiple",
    title: "Dedicated Proxy in Exchangable USA Location",
    available: true,
  },
  {
    id: "single",
    title: "Dedicated Proxy in Fixed USA Location",
    available: true,
  },
]

class ProductStore extends Container {
  constructor(props) {
    super(props)

    this.setProductTiers = debounce(this.setProductTiers)
  }

  meta = {
    fetching: false,
  }

  state = {
    multilocations: [],
    locations: [],
    selectedTier2Id: null,
    selectedTier3Id: null,
    locationID: null,
    selectedBillingCycle: null,
    billingCycles: [...BILLING_CYCLES],
    proxyLocationTypes: [...PROXY_LOCATION_TYPES],
    selectedProxyLocationType: "single",
    selectedMultilocation: null,
    notLaunchedMultilocations: [],
    multilocationProductsAvailability: true,
  }

  get proxyTypes() {
    const { locations, locationID } = this.state

    if (!locationID || locations.length === 0) return []

    return get(
      locations.find(({ id }) => id === locationID),
      "features",
      []
    )
  }

  get notLaunchedProxyTypes() {
    const { locations, locationID } = this.state

    if (!locationID || locations.length === 0) return []
    return get(
      locations.find(({ id }) => id === locationID),
      "notYetLaunchedFeatures",
      []
    )
  }

  get ipRotations() {
    const { locations, locationID } = this.state

    if (!locationID || locations.length === 0) return []

    return get(
      locations.find(({ id }) => id === locationID),
      "ipRotationFeatures",
      []
    )
  }

  get multilocationProxyTypes() {
    const { selectedMultilocation, locations } = this.state
    if (!selectedMultilocation || locations.length === 0) return []
    const selectedDataMode = selectedMultilocation.code.includes("4G")
      ? "4G"
      : "5G"
    let total = []
    locations.forEach(location => {
      let features = get(location, "features", [])
      features.forEach(proxyType => {
        if (proxyType.dataMode && proxyType.dataMode === selectedDataMode) {
          total.push(proxyType)
        }
      })
    })
    return total
  }

  fetchLocations = async () => {
    if (this.meta.fetching) return
    this.meta.fetching = true
    const res = await api.get("/active_locations")
    this.meta.fetching = false
    let locations = await deserialize(res.data)
    let multilocations = await deserialize(res.data?.multilocation_products)
    let notLaunchedMultilocations = await deserialize(
      res.data?.not_yet_launched_multilocation_products
    )
    let multilocationProductsAvailability =
      res.data?.multilocation_products_availability ?? true

    if (locations.length === 0) {
      return console.error(`No locations found!`)
    }

    locations = locations.map(l => ({
      ...l,
      ipRotationFeatures: l.ipRotationFeatures.map(i => ({
        ...i,
        available: true,
      })),
    }))
    await this.setState({
      locations,
      multilocations,
      notLaunchedMultilocations,
      multilocationProductsAvailability,
    })

    const location = locations
      .slice(1)
      .reduce(
        (location, i) =>
          i.typesAvailable > location.typesAvailable ? i : location,
        locations[0]
      )

    // Set 5G product as default multilocation product
    const multilocation5G = multilocations.find(product =>
      product.title.includes("5G")
    )
    this.setMultilocation(
      multilocation5G
        ? multilocation5G
        : multilocations[0] ?? notLaunchedMultilocations[0] ?? null
    )
    return this.setLocation(location)
  }

  setLocation = async ({ id, features, ipRotationFeatures } = {}) => {
    if (id !== this.state.locationID) {
      await this.setState({
        locationID: id,
      })
    }

    if (features.length === 0) {
      this.setState({
        selectedTier2Id: null,
        billingCycles: [...BILLING_CYCLES],
      })
    } else {
      const feature = features.find(({ available }) => available)
      if (feature) {
        this.setSelectedTier2Id(get(feature, "id"))
      } else {
        this.setState({
          selectedTier2Id: null,
          billingCycles: [...BILLING_CYCLES],
        })
      }
    }

    // Set IP Rotation ID (selectedTier3ID)
    if (features.length === 0 || ipRotationFeatures.length === 0) {
      this.setState({ selectedTier3Id: null })
    }
    if (!this.state.selectedTier3Id) {
      if (this.state.selectedProxyLocationType === "multiple") {
        const manualRotation = ipRotationFeatures.find(
          feat => feat.code.toLowerCase() === "manual"
        )
        this.setSelectedTier3Id(manualRotation ? manualRotation.id : null)
      } else {
        this.setSelectedTier3Id(get(ipRotationFeatures, "0.id"))
      }
    }

    // Set billing cycle
    this.setDefaultBillingCycle() // set billing cycle to monthly on initialize
  }

  setMultilocation = async location => {
    if (location?.id !== this.state.selectedMultilocation?.id) {
      this.setState({
        selectedMultilocation: location,
      })
    }
    // this.setDefaultBillingCycle(); // set billing cycle to monthly on initialize
  }

  setSelectedTier2Id = async (id = this.state.selectedTier2Id) => {
    if (id !== this.state.selectedTier2Id) {
      const feature = this.proxyTypes
        .filter(getAvailable)
        .find(p => p.id === id)

      if (feature) {
        const isWeeklyAvailable = get(feature, "weeklyPrice", null)
        this.setState({
          billingCycles: isWeeklyAvailable
            ? [...BILLING_CYCLES]
            : [BILLING_CYCLES[1]],
          // selectedBillingCycle: null,
          // ...(!isWeeklyAvailable && { selectedBillingCycle: "monthly" }),
        })
        this.setDefaultBillingCycle()
      }
      return this.setState({
        selectedTier2Id: get(feature, "id"),
      })
    }
    this.setSelectedTier3Id()
  }

  setSelectedTier3Id = async (id = this.state.selectedTier3Id) => {
    if (id !== this.state.selectedTier3Id) {
      const rotation = this.ipRotations.find(p => p.id === id)

      return this.setState({
        selectedTier3Id: get(rotation, "id"),
      })
    }
  }

  setBillingCycle = async code => {
    if (code !== this.state.billingCycle) {
      const billingCycle = this.state.billingCycles.find(p => p.code === code)
      return this.setState({
        selectedBillingCycle: get(billingCycle, "code"),
      })
    }
  }

  setProxyLocationType = async id => {
    this.setState({
      selectedProxyLocationType: id,
    })

    // Set location, tier2Id(available proxytype) and tier3Id(manual) whenever user swtich to multilocation type
    const { locations } = this.state
    const location = locations
      .slice(1)
      .reduce(
        (location, i) =>
          i.typesAvailable > location.typesAvailable ? i : location,
        locations[0]
      )
    await this.setState({
      locationID: location.id,
    })
    const features = get(location, "features", [])

    const availableFeature = features.find(({ available }) => available)
    const feature = availableFeature ? availableFeature : features[0]

    this.setState({
      selectedTier2Id:
        !availableFeature && id === "single" ? null : get(feature, "id"),
    })
    const ipRotationFeatures = get(location, "ipRotationFeatures", [])
    const manualRotation = ipRotationFeatures.find(
      feat => feat.code.toLowerCase() === "manual"
    )
    if (manualRotation) {
      this.setSelectedTier3Id(manualRotation.id)
    }
    return
  }

  setProductTiers = async ({
    selectedTier2Id,
    selectedTier3Id,
    locationID,
    multilocationId,
  }) => {
    try {
      const res = await api.get("/active_locations")
      const locations = await deserialize(res.data)
      const multilocations = await deserialize(res.data?.multilocation_products)
      if (locations.length === 0) {
        return console.error(`No locations found!`)
      }
      let selectedMultilocation = null
      if (multilocationId) {
        selectedMultilocation = multilocations.find(
          elem => elem.id === multilocationId
        )
      }

      await this.setState({
        locations,
        locationID,
        selectedTier2Id,
        selectedTier3Id,
        multilocations,
        selectedMultilocation,
      })
    } catch (e) {
      console.error(e)
    }
  }

  setDefaultBillingCycle = () => {
    if (!this.state.selectedBillingCycle) {
      const availableCycles = this.state.billingCycles.filter(
        cycle => cycle.available
      )
      const monthlyCycle = availableCycles.find(
        cycle => cycle.code === "monthly"
      )
      const defaultCycle =
        monthlyCycle?.code || availableCycles?.[0]?.code || null
      this.setState({ selectedBillingCycle: defaultCycle })
    }
  }
}

export default ProductStore
